import React, { useEffect, useState } from "react";
import Router, { useRouter } from "next/router";
import { useFormContext, useForm, FormProvider } from "react-hook-form";
import {
  Button,
  useSnackBar,
  Font,
} from "@moneyforward/mfx_biz_react_components";
import {
  GetBizsolEmailQuery,
  useGetBizsolEmailLazyQuery,
} from "@/generated/graphql";
import {
  KanaNameFormField,
  NameFormField,
  EmailFormField,
  PasswordFormField,
  TermsFormField,
} from "@/components/molecules";
import { passwordValidation } from "@/components/validations";
import config from "@/libs/config";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import { useIsSpMode } from "@/hooks";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  privacyPolicy: boolean;
  termOfUse: boolean;
  specialTerm: boolean;
  adEmailDelivery: boolean;
};

type Props = {
  onSubmit?: (v: any) => void;
  setValue?: (name: string, value: any) => void; // to set initial value dynamically
};

export const BizsolRegisterUserForm = ({ onSubmit, setValue }: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const isSpMode = useIsSpMode();
  const { handleSubmit, formState } = useFormContext();

  const [agreed, setAgreed] = useState(false);

  const { notify } = useSnackBar();

  const handleGetBizsolEmailCompleted = (data: GetBizsolEmailQuery) => {
    if (data) {
      data.bizSolUserInfo?.email &&
        setValue("email", data.bizSolUserInfo.email);
    }
  };

  const [getBizsolEmailQuery] = useGetBizsolEmailLazyQuery({
    onCompleted: handleGetBizsolEmailCompleted,
    onError: getNotifyErrorHandlerForGraphQL("Failed to get bizsol email"),
  });

  // Side effect to get email from bizsol
  const router = useRouter();
  useEffect(() => {
    if (router.isReady) {
      const { code } = router.query;
      const codeStr = Array.isArray(code) ? code[0] : code;
      getBizsolEmailQuery({
        variables: {
          registrationCode: codeStr,
        },
      });
    }
  }, [router.isReady]);

  // Side effect to set agreed
  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
  });
  const formValues = methods.getValues();
  useEffect(() => {
    setAgreed(
      formValues.privacyPolicy &&
        formValues.termOfUse &&
        formValues.specialTerm &&
        formValues.adEmailDelivery
    );
  }, [formValues]);

  return (
    <div css={container}>
      <form onSubmit={handleSubmit(onSubmit)} css={form}>
        <div css={!isSpMode ? box : spModeBox}>
          <div css={!isSpMode ? inputList : spModeInputList}>
            <div css={listItem}>
              <NameFormField
                nameLastName="lastName"
                nameFirstName="firstName"
              />
            </div>
            <div css={listItem}>
              <KanaNameFormField
                nameLastName="lastNameKana"
                nameFirstName="firstNameKana"
              />
            </div>
            <div css={listItem}>
              <EmailFormField name="email" labelRequired={true} />
            </div>
            <div css={listItem}>
              <PasswordFormField
                name="password"
                usePasswordMeter={true}
                registerOptions={{
                  validate: passwordValidation,
                }}
                requiredLabel={true}
              />
            </div>
          </div>
        </div>
        <FormProvider {...methods}>
          <TermsFormField
            privacyPolicy="privacyPolicy"
            termOfUse="termOfUse"
            specialTerm="specialTerm"
            adEmailDelivery="adEmailDelivery"
            usingExternalIdp={config.USING_EXTERNAL_IDP}
          ></TermsFormField>
        </FormProvider>
        <div css={labelWrapper}>
          <Font variant="m" component="p">
            <b>
              登録ボタン押下後、入力いただいたメールアドレス宛に、認証メールをお送りします。
              <br />
              後ほどメールボックスをご確認ください。
            </b>
          </Font>
        </div>
        <div css={buttonWrapper}>
          <Button
            type="submit"
            variant="primary"
            disabled={formState.isSubmitting || !agreed}
          >
            登録
          </Button>
        </div>
      </form>
    </div>
  );
};

const container = css({
  display: "flex",
  justifyContent: "center",
});

const box = css({
  width: "640px",
  minHeight: "245px",
  padding: "18px 32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  width: "100%",
  minHeight: "245px",
  padding: "15px 17px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const form = css({
  width: "640px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const inputList = css({
  flex: 1,
  paddingTop: "8px",
});

const spModeInputList = css({
  flex: 1,
});

const listItem = css({
  marginBottom: "15px",
});

const buttonWrapper = css({
  display: "flex",
  justifyContent: "center",
  marginTop: "24px",
});

const labelWrapper = css({
  width: "640px",
  padding: "24px",
  backgroundColor: color.gray50,
  marginTop: "24px",
  borderRadius: "2px",
});
